import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import usePageMeta from "@/hooks/usePageMeta";
import { getMovieById, getRelatedMovies } from "@/lib/movies";
import DetailHero from "./components/DetailHero";
import PlotSection from "./components/PlotSection";
import EpisodePanel from "./components/EpisodePanel";
import DetailSidebar from "./components/DetailSidebar";
import RelatedSection from "./components/RelatedSection";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(id);

  usePageMeta(
    movie ? `${movie.title}剧情介绍与分析 - 淫乱小镇` : "作品未找到 - 淫乱小镇",
    movie
      ? `${movie.title}（${movie.year}，${movie.country}）剧情介绍：${movie.summary}`
      : "没有找到对应的影视作品，可能链接有误，请返回影视分类页重新浏览。",
    movie
      ? `淫乱小镇,${movie.title},剧情介绍,影视分析,${movie.genres.join(",")}`
      : "淫乱小镇,影视作品,剧情介绍",
  );

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="pt-16 md:pt-20">
          <div className="max-w-shell mx-auto px-4 md:px-8 py-20 md:py-28 flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-background-200/70 flex items-center justify-center text-foreground-500 mb-5">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50">
              没有找到这部作品
            </h1>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-500">
              链接里的作品编号可能已经失效，或者内容已经调整。你可以回到影视分类页，从完整作品库里重新找。
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link
                to="/category"
                className="h-11 px-6 inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 text-foreground-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                返回影视分类
              </Link>
              <Link
                to="/search"
                className="h-11 px-6 inline-flex items-center justify-center gap-2 rounded-md border border-background-300 text-foreground-200 text-sm hover:border-accent-500 hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                去搜索作品
              </Link>
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelatedMovies(movie, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main className="pt-16 md:pt-20">
        <section className="relative border-b border-background-200/70 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={movie.still}
              alt={`${movie.title}剧照背景`}
              title={`${movie.title}剧情介绍与分析`}
              className="w-full h-full object-cover object-top opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background-50/80 via-background-50/92 to-background-50"></div>
          </div>

          <div className="relative max-w-shell mx-auto px-4 md:px-8 py-7 md:py-10">
            <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-2 text-xs text-foreground-600 mb-5 md:mb-6">
              <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-700"></i>
              <Link to="/category" className="hover:text-accent-500 transition-colors cursor-pointer">
                影视分类
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-700"></i>
              <Link
                to={`/category?type=${encodeURIComponent(movie.type)}`}
                className="hover:text-accent-500 transition-colors cursor-pointer"
              >
                {movie.type}
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-700"></i>
              <span className="text-foreground-300 truncate max-w-[160px]">{movie.title}</span>
            </nav>

            <DetailHero movie={movie} />
          </div>
        </section>

        <section className="max-w-shell mx-auto px-4 md:px-8 py-8 md:py-10">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-7 lg:gap-9 items-start">
            <div className="min-w-0">
              <PlotSection movie={movie} />
              {movie.episodes && <EpisodePanel episodes={movie.episodes} title={movie.title} />}
            </div>
            <DetailSidebar movie={movie} />
          </div>

          <RelatedSection movies={related} />
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}